import "./Roadmap.scss";
import { useEffect, useRef } from "react";

import Phase1Image from "../../assets/images/roadmap/phase-1.svg";
import Phase2Image from "../../assets/images/roadmap/phase-2.svg";
import Phase3Image from "../../assets/images/roadmap/phase-3.svg";
import Phase4Image from "../../assets/images/roadmap/phase-4.svg";

const roadmapPhases = [
	{
		title: "Concept & Planning",
		image: Phase1Image,
		description: [
			"Finalize Vernus AI vision and goals",
			"Develop whitepaper and initial website prototype",
			"Build community through social media and early engagement",
		],
	},
	{
		title: "Development & Beta Testing",
		image: Phase2Image,
		description: [
			"Launch MVP (Minimum Viable Product) with core AI tools (Codex, Sketchy, Doc).",
			"Open beta testing with select users.",
			"Implement feedback-driven improvements.",
		],
	},
	{
		title: "Full Platform Launch",
		image: Phase3Image,
		description: [
			"Enhance platform scalability and security",
			"Expand AI capabilities with additional tools",
			"Marketing campaign and partnerships",
		],
	},
	{
		title: "Expansion & Token Integration",
		image: Phase4Image,
		description: [
			"Introduce token-based economy for platform access",
			"Expand partnerships and community incentives",
			"Develop long-term vision roadmap",
		],
	},
];

export default function Roadmap() {
	const desktopRoadmapRef = useRef(null);
	const desktopProgressBarRef = useRef(null);
	const mobileRoadmapRef = useRef(null);
	const mobileProgressLabelRef = useRef(null);

	useEffect(() => {
		// Function to calculate progress for mobile roadmap
		const calculateMobileProgress = (element) => {
			if (!element) return 0;

			const rect = element.getBoundingClientRect();
			const windowHeight = window.innerHeight;

			// Start when the top of the element enters the viewport
			// End when the bottom of the element leaves the viewport
			const startPosition = windowHeight; // When top enters viewport
			const endPosition = 0 - rect.height; // When bottom leaves viewport
			const currentPosition = rect.top;

			// Calculate progress percentage (0-100)
			const totalDistance = startPosition - endPosition;
			const currentProgress = startPosition - currentPosition;
			const progressPercentage = Math.min(
				100,
				Math.max(0, (currentProgress / totalDistance) * 100)
			);

			return progressPercentage;
		};

		// Function to calculate progress for desktop roadmap focusing on the progress bar
		const calculateDesktopProgress = () => {
			if (!desktopRoadmapRef.current || !desktopProgressBarRef.current)
				return 0;

			const roadmapRect =
				desktopRoadmapRef.current.getBoundingClientRect();
			const progressBarRect =
				desktopProgressBarRef.current.getBoundingClientRect();
			const windowHeight = window.innerHeight;

			// Calculate progress based on the progress bar's position, not the entire roadmap
			// Start when the progress bar enters the viewport (from bottom)
			// End when the progress bar leaves the viewport (from top)
			const startPosition = windowHeight; // When progress bar enters viewport
			const endPosition = 0; // When progress bar leaves viewport from the top
			const currentPosition = progressBarRect.top;

			// If the progress bar is not yet visible or has passed entirely, cap the progress
			if (progressBarRect.top > windowHeight) return 0;
			if (progressBarRect.bottom < 0) return 100;

			// Calculate progress percentage (0-100)
			const totalDistance = startPosition - endPosition;
			const currentProgress = startPosition - currentPosition;
			const progressPercentage = Math.min(
				100,
				Math.max(0, (currentProgress / totalDistance) * 100)
			);

			return progressPercentage;
		};

		// Function to update desktop roadmap
		const updateDesktopRoadmap = () => {
			if (!desktopRoadmapRef.current) return;

			const progress = calculateDesktopProgress();
			desktopRoadmapRef.current.style.setProperty("--progress", progress);

			// Update active phases based on progress
			const progressLabels = desktopRoadmapRef.current.querySelectorAll(
				".roadmap-progress-label"
			);

			// Get roadmap items from top and bottom sections to apply correct sequence
			const topItems = desktopRoadmapRef.current.querySelectorAll(
				".roadmap-top .roadmap-item"
			);
			const bottomItems = desktopRoadmapRef.current.querySelectorAll(
				".roadmap-bottom .roadmap-item"
			);

			// Calculate active phase (0-3)
			const activePhaseIndex = Math.min(3, Math.floor(progress / 25));

			// Update progress labels
			progressLabels.forEach((label, index) => {
				if (index <= activePhaseIndex) {
					label.classList.add("active");
				} else {
					label.classList.remove("active");
				}
			});

			// Apply correct unfading sequence:
			// Phase 1 (top[0]) -> Phase 2 (bottom[0]) -> Phase 3 (top[1]) -> Phase 4 (bottom[1])

			// Phase 1 (always unfaded if progress started)
			if (activePhaseIndex >= 0 && topItems[0]) {
				topItems[0].classList.remove("faded");
			}

			// Phase 2
			if (activePhaseIndex >= 1 && bottomItems[0]) {
				bottomItems[0].classList.remove("faded");
			} else if (bottomItems[0]) {
				bottomItems[0].classList.add("faded");
			}

			// Phase 3
			if (activePhaseIndex >= 2 && topItems[1]) {
				topItems[1].classList.remove("faded");
			} else if (topItems[1]) {
				topItems[1].classList.add("faded");
			}

			// Phase 4
			if (activePhaseIndex >= 3 && bottomItems[1]) {
				bottomItems[1].classList.remove("faded");
			} else if (bottomItems[1]) {
				bottomItems[1].classList.add("faded");
			}
		};

		// Function to update mobile roadmap
		const updateMobileRoadmap = () => {
			if (!mobileRoadmapRef.current || !mobileProgressLabelRef.current)
				return;

			const progress = calculateMobileProgress(mobileRoadmapRef.current);
			mobileRoadmapRef.current.style.setProperty("--progress", progress);

			// Update mobile phase label and items
			const mobileItems =
				mobileRoadmapRef.current.querySelectorAll(".roadmap-item");
			const activePhaseIndex = Math.min(3, Math.floor(progress / 25));

			// Update the phase label text
			mobileProgressLabelRef.current.textContent = `Phase ${
				activePhaseIndex + 1
			}`;

			mobileItems.forEach((item, index) => {
				if (index <= activePhaseIndex) {
					item.classList.remove("faded");
				} else {
					item.classList.add("faded");
				}
			});
		};

		// Combined scroll handler
		const handleScroll = () => {
			updateDesktopRoadmap();
			updateMobileRoadmap();
		};

		// Add scroll event listener
		window.addEventListener("scroll", handleScroll);

		// Initial calculation on mount
		handleScroll();

		// Clean up event listener on component unmount
		return () => {
			window.removeEventListener("scroll", handleScroll);
		};
	}, []);

	return (
		<div className="roadmap">
			<div className="roadmap-content-wrapper content-wrapper-lg">
				<div className="roadmap-content">
					<h3 className="roadmap-title" data-aos="fade-up">
						<span className="title-txt">Roadmap</span>
					</h3>

					<div
						className="roadmap-desktop"
						ref={desktopRoadmapRef}
						style={{ "--progress": 0 }}
					>
						<div className="roadmap-top">
							<div className="roadmap-items">
								{roadmapPhases
									.slice(0, 2)
									.map((phase, index) => (
										<RoadmapItem
											key={index}
											phase={phase}
										/>
									))}
							</div>
						</div>

						<div className="roadmap-middle">
							<div className="roadmap-progress-wrapper">
								<div
									className="roadmap-progress"
									ref={desktopProgressBarRef}
								>
									<div className="roadmap-progress-bar"></div>

									<div className="roadmap-progress-labels">
										<div className="roadmap-progress-label active">
											Phase 1
										</div>
										<div className="roadmap-progress-label">
											Phase 2
										</div>
										<div className="roadmap-progress-label">
											Phase 3
										</div>
										<div className="roadmap-progress-label">
											Phase 4
										</div>
									</div>
								</div>
							</div>
						</div>

						<div className="roadmap-bottom">
							<div className="roadmap-items">
								{roadmapPhases
									.slice(2, 4)
									.map((phase, index) => (
										<RoadmapItem
											key={index}
											phase={phase}
										/>
									))}
							</div>
						</div>
					</div>

					<div
						className="roadmap-mobile"
						ref={mobileRoadmapRef}
						style={{ "--progress": 0 }}
					>
						<div className="roadmap-left">
							<div className="roadmap-progress-wrapper">
								<div className="roadmap-progress">
									<div className="roadmap-progress-inner">
										<div className="roadmap-progress-bar"></div>
									</div>
								</div>

								<div
									className="roadmap-progress-bar-label"
									ref={mobileProgressLabelRef}
								>
									Phase 1
								</div>
							</div>
						</div>

						<div className="roadmap-right">
							<div className="roadmap-items">
								{roadmapPhases.map((phase, index) => (
									<RoadmapItem key={index} phase={phase} />
								))}
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

function RoadmapItem({ phase }) {
	return (
		<div
			className={`roadmap-item ${
				phase.title === "Concept & Planning" ? "" : "faded"
			}`}
		>
			<div className="roadmap-card">
				<div className="roadmap-card-icon">
					<img src={phase.image} alt={phase.title} />
				</div>
				<div className="roadmap-card-content">
					<div className="roadmap-card-title">{phase.title}</div>
					<ul className="roadmap-card-list">
						{phase.description.map((point, index) => (
							<li key={index}>{point}</li>
						))}
					</ul>
				</div>
			</div>
		</div>
	);
}
